export const SRHT_COURSE_WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function parseIsoDate(value) {
  const rawValue = String(value || '').trim();
  const match = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const dayOfMonth = Number(match[3]);
  const date = new Date(year, monthIndex, dayOfMonth);
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== monthIndex
    || date.getDate() !== dayOfMonth
  ) {
    return null;
  }

  return date;
}

function formatIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getMondayBasedWeekdayIndex(date) {
  return (date.getDay() + 6) % 7;
}

export function isIsoDateWithinRange(value, startDate, endDate) {
  const parsedDate = parseIsoDate(value);
  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  if (!parsedDate || !parsedStartDate || !parsedEndDate) {
    return false;
  }

  const dateKey = formatIsoDate(parsedDate);
  const startKey = formatIsoDate(parsedStartDate);
  const endKey = formatIsoDate(parsedEndDate);
  if (startKey > endKey) {
    return false;
  }

  return dateKey >= startKey && dateKey <= endKey;
}

export function toggleIsoDateSelection(selectedDates = [], value) {
  const parsedDate = parseIsoDate(value);
  if (!parsedDate) {
    return [...new Set(selectedDates)].sort((left, right) => left.localeCompare(right));
  }

  const isoDate = formatIsoDate(parsedDate);
  const selectedSet = new Set(selectedDates);
  if (selectedSet.has(isoDate)) {
    selectedSet.delete(isoDate);
  } else {
    selectedSet.add(isoDate);
  }

  return [...selectedSet].sort((left, right) => left.localeCompare(right));
}

export function buildSrhtCourseDayCalendarMonths(startDate, endDate, selectedDates = []) {
  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);
  if (!parsedStartDate || !parsedEndDate) {
    return [];
  }

  const startKey = formatIsoDate(parsedStartDate);
  const endKey = formatIsoDate(parsedEndDate);
  if (startKey > endKey) {
    return [];
  }

  const selectedSet = new Set(selectedDates.filter((value) => isIsoDateWithinRange(value, startDate, endDate)));
  const months = [];
  const cursor = new Date(parsedStartDate.getFullYear(), parsedStartDate.getMonth(), 1);
  const lastMonth = new Date(parsedEndDate.getFullYear(), parsedEndDate.getMonth(), 1);

  while (cursor <= lastMonth) {
    const cells = [];
    const firstDayOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const lastDayOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const leadingPadding = getMondayBasedWeekdayIndex(firstDayOfMonth);

    for (let index = 0; index < leadingPadding; index += 1) {
      cells.push(null);
    }

    for (let dayOfMonth = 1; dayOfMonth <= lastDayOfMonth.getDate(); dayOfMonth += 1) {
      const date = new Date(cursor.getFullYear(), cursor.getMonth(), dayOfMonth);
      const isoDate = formatIsoDate(date);
      const isInRange = isoDate >= startKey && isoDate <= endKey;

      cells.push({
        isoDate,
        dayOfMonth,
        isInRange,
        isSelected: isInRange && selectedSet.has(isoDate),
      });
    }

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    months.push({
      monthKey: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
      label: cursor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
      weeks: Array.from({ length: cells.length / 7 }, (_, index) => cells.slice(index * 7, index * 7 + 7)),
    });

    cursor.setMonth(cursor.getMonth() + 1, 1);
  }

  return months;
}